import { EventType } from "@/analytics/analytics-events";
import { PredefinedViewEventProperties } from "@/analytics/camera-menu-events";
import {
  CAMERA_DIRECTIONS,
  CameraDirection,
  CameraViewType,
} from "@/utils/cam-view";
import {
  Checkmark2Icon,
  cyan,
  FaroMenu,
  FaroMenuItem,
  FaroText,
  FaroTooltip,
  grey,
  OrthographicCameraIcon,
  PerspectiveCameraIcon,
  Toolbar,
  ToolButton,
  ViewPredefinedBackIcon,
  ViewPredefinedBottomIcon,
  ViewPredefinedFrontIcon,
  ViewPredefinedLeftIcon,
  ViewPredefinedRightIcon,
  ViewPredefinedTopIcon,
  ViewsIcon,
  ZoomAllIcon,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { Stack, Tooltip } from "@mui/material";
import { RefObject, useCallback, useRef, useState, type JSX } from "react";
import { SmartNavigationToggle } from "./smart-navigation-toggle";

type CameraSwitchProps = {
  /**
   * Whether to show the smart navigation toggle.
   *
   * @default false
   */
  shouldShowSmartNavigationToggle?: boolean;
  /**
   * Whether to show the projection options.
   *
   * @default true
   */
  shouldShowProjectionOption?: boolean;

  /** The currently active camera in 3D projection. */
  cameraOptions3D: CameraViewType;

  /** A callback to execute when the user changes the camera in 3D. */
  onCameraChange(camera: CameraViewType): void;

  /** Whether buttons for the six camera predefined views should be shown */
  showPredefinedViewsOptions?: boolean;

  /** The predefined view */
  predefinedView?: CameraDirection;

  /** Optional callback issued when the user selects a predefined view */
  onPredefinedViewSelected?(direction: CameraDirection): void;

  /** If defined, a button lets the user bring the camera back to the default view */
  onRecenterView?(): void;
};

/** Map for which icon should be used for which direction */
const directionsToIcon = {
  front: ViewPredefinedFrontIcon,
  back: ViewPredefinedBackIcon,
  right: ViewPredefinedRightIcon,
  left: ViewPredefinedLeftIcon,
  top: ViewPredefinedTopIcon,
  bottom: ViewPredefinedBottomIcon,
};

/** @returns A toolbar to change the camera in 3D projection */
export function CameraSwitch({
  shouldShowSmartNavigationToggle = false,
  shouldShowProjectionOption = true,
  cameraOptions3D,
  onCameraChange,
  showPredefinedViewsOptions,
  predefinedView,
  onPredefinedViewSelected,
  onRecenterView,
}: CameraSwitchProps): JSX.Element | null {
  const changeCamera = useCallback(() => {
    if (cameraOptions3D === CameraViewType.perspective) {
      Analytics.track(EventType.enableOrthographicCamera);
      onCameraChange(CameraViewType.orthographic);
    } else {
      Analytics.track(EventType.enablePerspectiveCamera);
      onCameraChange(CameraViewType.perspective);
    }
  }, [onCameraChange, cameraOptions3D]);

  const directionMenuBtnRef = useRef<HTMLButtonElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  if (
    !shouldShowSmartNavigationToggle &&
    !shouldShowProjectionOption &&
    !onRecenterView
  ) {
    return null;
  }

  return (
    <>
      <Toolbar>
        {onRecenterView && (
          <FaroTooltip title="Recenter view" placement="right">
            <ToolButton aria-label="recenter view" onClick={onRecenterView}>
              <ZoomAllIcon sx={{ width: "24px", height: "24px" }} />
            </ToolButton>
          </FaroTooltip>
        )}
        {shouldShowProjectionOption && showPredefinedViewsOptions && (
          <FaroTooltip title="Select camera direction" placement="right">
            <ToolButton
              ref={directionMenuBtnRef}
              onClick={() => setIsMenuOpen(true)}
              selected={isMenuOpen}
              aria-label="select camera direction"
            >
              <CameraDirectionIcon direction={predefinedView} />
            </ToolButton>
          </FaroTooltip>
        )}

        {shouldShowSmartNavigationToggle && <SmartNavigationToggle />}

        {shouldShowProjectionOption && (
          <Tooltip
            title={`Switch to ${cameraOptions3D === CameraViewType.perspective ? "orthographic" : "perspective"} camera`}
            placement="top"
          >
            <ToolButton
              onClick={changeCamera}
              aria-label="camera toggle"
              value={cameraOptions3D}
            >
              {cameraOptions3D === CameraViewType.perspective && (
                <PerspectiveCameraIcon />
              )}
              {cameraOptions3D === CameraViewType.orthographic && (
                <OrthographicCameraIcon />
              )}
            </ToolButton>
          </Tooltip>
        )}
      </Toolbar>

      {shouldShowProjectionOption && showPredefinedViewsOptions && (
        <CameraSideMenu
          activeCameraDirection={predefinedView}
          onSelect={(direction: CameraDirection) => {
            setIsMenuOpen(false);
            onPredefinedViewSelected?.(direction);
          }}
          onClose={() => setIsMenuOpen(false)}
          anchorEl={directionMenuBtnRef}
          open={isMenuOpen}
        />
      )}
    </>
  );
}

type ActiveCameraDirectionIconProps = {
  /** The direction to get the corresponding icon for */
  direction?: CameraDirection;
};

function CameraDirectionIcon({
  direction,
}: ActiveCameraDirectionIconProps): JSX.Element {
  const Icon = direction ? directionsToIcon[direction] : ViewsIcon;
  return <Icon />;
}

type CameraSideMenuProps = {
  /** Currently active direction, used to mark the corresponding menu item as active */
  activeCameraDirection?: CameraDirection;

  /** The element to anchor the menu to */
  anchorEl: RefObject<HTMLButtonElement | null>;

  /** If true, the menu is shown */
  open: boolean;

  /** Callback for selection an element in the menu */
  onSelect(direction: CameraDirection): void;

  /** Callback for then the user is clicking outside of the menu */
  onClose(): void;
};

function CameraSideMenu({
  anchorEl,
  activeCameraDirection,
  open,
  onSelect,
  onClose,
}: CameraSideMenuProps): JSX.Element {
  const predefinedViewSelected = useCallback(
    (direction: CameraDirection) => {
      Analytics.track<PredefinedViewEventProperties>(
        EventType.selectCameraPredefinedView,
        {
          selectedView: direction,
        },
      );
      onSelect(direction);
    },
    [onSelect],
  );

  return (
    <FaroMenu
      anchorEl={anchorEl.current}
      open={open}
      dark
      minWidth="170px"
      sx={{
        marginLeft: 1,
      }}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      {CAMERA_DIRECTIONS.map((direction) => (
        <FaroMenuItem
          key={direction}
          onClick={() => predefinedViewSelected(direction)}
          Icon={directionsToIcon[direction]}
          dark
          color={direction === activeCameraDirection ? cyan[400] : grey[300]}
          aria-label={`${direction} view`}
          label={
            <Stack
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <FaroText
                variant="bodyM"
                dark
                sx={{
                  textTransform: "capitalize",
                  color: "inherit",
                }}
              >
                {direction} view
              </FaroText>

              {direction === activeCameraDirection && (
                <Checkmark2Icon sx={{ height: "20px" }} />
              )}
            </Stack>
          }
        />
      ))}
    </FaroMenu>
  );
}
